import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Pagination from 'common-ui/table/Pagination';
import {
  createProduct,
  deleteProduct,
  getProducts,
  updateProduct,
} from 'features/newsales/newsalesActions';
import {
  selectProducts,
  selectProductsCurrentPage,
  selectProductsDeleteSuccess,
  selectProductsLoading,
  selectProductsPagesCount,
  selectProductsSearchData,
  selectProductsSuccess,
  selectProductsUpdateSuccess,
  setProductsCreateSuccess,
  setProductsCurrentPage,
  setProductsDeleteSuccess,
  setProductsSearchData,
  setProductsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import LeadSourceImage from '../components/LeadSourceImage';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';
import SearchFilter from '../components/SearchFilter';
import {
  CardsGrid,
  EmptyState,
  LoadingSpinner,
  PaginationWrapper,
  ProductsContainer,
  ProductsContent,
  ProductsTitle,
} from './Products.styles';

const Products = () => {
  const dispatch = useDispatch();

  const products = useSelector(selectProducts);
  const loading = useSelector(selectProductsLoading);
  const currentPage = useSelector(selectProductsCurrentPage);
  const pagesCount = useSelector(selectProductsPagesCount);
  const searchData = useSelector(selectProductsSearchData);
  const createSuccess = useSelector(selectProductsSuccess);
  const updateSuccess = useSelector(selectProductsUpdateSuccess);
  const deleteSuccess = useSelector(selectProductsDeleteSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const params = {
      status: searchData.status,
      page: currentPage,
      limit: searchData.limit,
      name: searchData.search,
    };
    dispatch(getProducts(params));
  }, [dispatch, currentPage, searchData.status, searchData.limit, searchData.search]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setSelectedProduct(null);
      dispatch(setProductsCreateSuccess(false));
      const params = {
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        search: searchData.search,
      };
      dispatch(getProducts(params));
    }
  }, [createSuccess, dispatch, currentPage, searchData]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setSelectedProduct(null);
      dispatch(setProductsUpdateSuccess(false));
      const params = {
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        search: searchData.search,
      };
      dispatch(getProducts(params));
    }
  }, [updateSuccess, dispatch, currentPage, searchData]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setProductsDeleteSuccess(false));

      // If deleting the last item on a page (and not on page 1), go to previous page
      const shouldGoToPreviousPage = products?.length === 1 && currentPage > 1;

      if (shouldGoToPreviousPage) {
        dispatch(setProductsCurrentPage(currentPage - 1));
      } else {
        // Refetch current page
        const params = {
          status: searchData.status,
          page: currentPage,
          limit: searchData.limit,
          search: searchData.search,
        };
        dispatch(getProducts(params));
      }
    }
  }, [deleteSuccess, dispatch, products?.length, currentPage, searchData]);

  const handleSearchChange = (value) => {
    dispatch(setProductsSearchData({ search: value }));
    dispatch(setProductsCurrentPage(1));
  };
  const handleStatusChange = (value) => {
    dispatch(setProductsSearchData({ status: value, offset: 0 }));
    dispatch(setProductsCurrentPage(1));
  };

  const handlePageChange = (page) => {
    dispatch(setProductsCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setProductsSearchData({ limit: count, offset: 0 }));
    dispatch(setProductsCurrentPage(1));
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const productToEdit = products.find((product) => product.id === id);
    if (productToEdit) {
      setModalMode('edit');
      setSelectedProduct(productToEdit);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSubmit = (idOrData, productData) => {
    if (modalMode === 'create') {
      dispatch(createProduct(idOrData));
    } else {
      dispatch(updateProduct({ id: idOrData, data: productData }));
    }
  };

  const handleDeleteProduct = (id) => {
    dispatch(deleteProduct(id));
  };

  return (
    <ProductsContainer>
      <ProductsTitle>Products</ProductsTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search products..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        createButtonText="+ Create Product"
      />
      <ProductsContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !products || products?.length === 0 ? (
          <EmptyState>No products found</EmptyState>
        ) : (
          <>
            <CardsGrid>
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  icon={
                    <LeadSourceImage imagePath={product.image} alt={product.name} size={48} />
                  }
                  title={product.name}
                  status={product.enabled ? 'Enabled' : 'Disabled'}
                  description={product.description || 'No description'}
                  onEdit={() => handleOpenEditModal(product.id)}
                  onDelete={() => handleDeleteProduct(product.id)}
                />
              ))}
            </CardsGrid>

            {pagesCount > 1 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagesCount}
                  onPageChange={handlePageChange}
                  handleRowCountChange={handleRowCountChange}
                  dataCount={products?.length}
                  count={searchData.limit}
                  editableRowCount
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </ProductsContent>

      <ProductModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedProduct}
      />
    </ProductsContainer>
  );
};

export default Products;
